import { useState } from "react";
import { AppAction, UserRole, type AuthUser } from "@edlab-fms/shared";
import { ApiClientError } from "../../lib/api/client";
import {
  useApproveExpenseMutation,
  useExpensePaymentsQuery,
  useExpenseQuery,
  useReceiptQuery,
  useReversePaymentMutation,
  useReverseReceiptMutation,
  useReverseExpenseMutation,
  useSubmitExpenseMutation,
  useWithdrawExpenseMutation,
  type TransactionListItem,
} from "../../lib/api/transactions";
import { fileDownloadUrl, useDeleteFileMutation } from "../../lib/api/files";
import { useBankAccountsQuery } from "../../lib/api/reference";
import { formatMoney } from "../../lib/format/money";
import { expenseStatusTone, formatExpenseStatus } from "../../lib/format/status";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Modal } from "../../components/ui/Modal";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../../components/ui/Table";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import { useReferenceMaps, type UnitOption } from "./transaction-utils";
import { AttachEvidenceModal } from "./AttachEvidenceModal";
import { EditExpenseModal } from "./EditExpenseModal";
import { RecordPaymentModal } from "./RecordPaymentModal";
import { RejectExpenseModal } from "./RejectExpenseModal";

interface TransactionDetailModalProps {
  item: TransactionListItem | null;
  onClose: () => void;
  user: AuthUser;
  units: UnitOption[];
  funds: { id: string; name: string }[];
  incomeCategories: { id: string; name: string }[];
  expenseCategories: { id: string; name: string }[];
}

type SubModal = "payment" | "reject" | "evidence" | "edit" | null;

export function TransactionDetailModal({
  item,
  onClose,
  user,
  units,
  funds,
  incomeCategories,
  expenseCategories,
}: TransactionDetailModalProps) {
  const expenseQuery = useExpenseQuery(item?.kind === "expense" ? item.id : null);
  const receiptQuery = useReceiptQuery(item?.kind === "income" ? item.id : null);
  const paymentsQuery = useExpensePaymentsQuery(
    item?.kind === "expense" ? item.id : null,
  );
  const banks = useBankAccountsQuery(item?.kind === "expense");

  const submitExpense = useSubmitExpenseMutation();
  const approveExpense = useApproveExpenseMutation();
  const reverseReceipt = useReverseReceiptMutation();
  const reversePayment = useReversePaymentMutation();
  const withdrawExpense = useWithdrawExpenseMutation();
  const reverseExpense = useReverseExpenseMutation();
  const deleteFile = useDeleteFileMutation();

  const [subModal, setSubModal] = useState<SubModal>(null);
  const [reverseReason, setReverseReason] = useState("");
  const [expenseReverseReason, setExpenseReverseReason] = useState("");
  const [paymentReverseReason, setPaymentReverseReason] = useState<Record<string, string>>({});
  const [actionError, setActionError] = useState<string | null>(null);

  const maps = useReferenceMaps(
    units,
    funds,
    item?.kind === "income" ? incomeCategories : expenseCategories,
    banks.data?.bankAccounts,
  );

  if (!item) {
    return null;
  }

  const expense = expenseQuery.data?.expense;
  const receipt = receiptQuery.data?.receipt;
  const loading =
    item.kind === "expense" ? expenseQuery.isLoading : receiptQuery.isLoading;
  const fullAccess = !item.partial;
  const isSuperAdmin = user.role === UserRole.SUPER_ADMIN;
  const canEdit =
    isSuperAdmin ||
    user.role === UserRole.ACCOUNTS ||
    user.permissions.actions.includes(AppAction.EXPENSE_CREATE);

  const canSubmit =
    fullAccess &&
    expense &&
    user.permissions.actions.includes(AppAction.EXPENSE_SUBMIT) &&
    ["draft", "rejected"].includes(expense.status) &&
    (expense.createdById === user.id || isSuperAdmin);

  const canApprove =
    fullAccess &&
    expense &&
    user.permissions.actions.includes(AppAction.EXPENSE_APPROVE) &&
    expense.status === "submitted";

  const canPay =
    fullAccess &&
    expense &&
    user.permissions.actions.includes(AppAction.EXPENSE_PAY) &&
    ["approved", "part_paid"].includes(expense.status) &&
    Number.parseFloat(expense.unpaidAmount) > 0;

  const canAttachEvidence =
    fullAccess && expense && canEdit && user.permissions.actions.includes(AppAction.FILE_UPLOAD);

  const canModifyExpense =
    fullAccess &&
    expense &&
    canEdit &&
    ["draft", "rejected"].includes(expense.status) &&
    (expense.createdById === user.id || isSuperAdmin);

  const hasActivePayments =
    expense && Number.parseFloat(expense.paidAmount) > 0;

  const canWithdrawSubmitted =
    fullAccess &&
    expense &&
    expense.status === "submitted" &&
    expense.createdById === user.id &&
    user.permissions.actions.includes(AppAction.EXPENSE_SUBMIT);

  const canWithdrawApproved =
    fullAccess &&
    expense &&
    expense.status === "approved" &&
    !hasActivePayments &&
    user.permissions.actions.includes(AppAction.EXPENSE_REVERSE);

  const canReverseExpense =
    fullAccess &&
    expense &&
    expense.status === "approved" &&
    !hasActivePayments &&
    user.permissions.actions.includes(AppAction.EXPENSE_REVERSE);

  const canReverse =
    user.role === UserRole.SUPER_ADMIN || user.role === UserRole.ACCOUNTS;
  const canReverseReceipt = canReverse && receipt?.status === "posted";
  const payments = paymentsQuery.data?.payments ?? [];

  async function runAction(action: () => Promise<unknown>) {
    setActionError(null);
    try {
      await action();
    } catch (err) {
      setActionError(err instanceof ApiClientError ? err.message : "Action failed.");
    }
  }

  function refreshExpense() {
    void expenseQuery.refetch();
    void paymentsQuery.refetch();
  }

  const modalTitle =
    item.kind === "expense" && item.partial ? "Assigned expense detail" : item.reference;
  const modalDescription =
    item.kind === "expense" && item.partial
      ? "Authorized allocation lines only"
      : item.party;

  const footerActions = (
    <>
      {canModifyExpense ? (
        <Button
          variant="secondary"
          onClick={() => {
            setSubModal("edit");
          }}
        >
          Edit expense
        </Button>
      ) : null}
      {canAttachEvidence ? (
        <Button
          variant="secondary"
          onClick={() => {
            setSubModal("evidence");
          }}
        >
          {expense?.files.length ? "Replace file" : "Attach evidence"}
        </Button>
      ) : null}
      {canPay ? (
        <Button
          onClick={() => {
            setSubModal("payment");
          }}
        >
          Record payment
        </Button>
      ) : null}
      {canApprove ? (
        <>
          <Button
            variant="danger"
            onClick={() => {
              setSubModal("reject");
            }}
          >
            Reject
          </Button>
          <Button
            disabled={approveExpense.isPending}
            onClick={() =>
              void runAction(() => approveExpense.mutateAsync(expense!.id))
            }
          >
            Approve expense
          </Button>
        </>
      ) : null}
      {canSubmit ? (
        <Button
          disabled={submitExpense.isPending}
          onClick={() =>
            void runAction(() =>
              submitExpense.mutateAsync({
                id: expense!.id,
                missingEvidenceReason: expense!.missingEvidenceReason ?? undefined,
              }),
            )
          }
        >
          Submit for approval
        </Button>
      ) : null}
      <Button variant="secondary" onClick={onClose}>
        Close
      </Button>
    </>
  );

  return (
    <>
      <Modal
        open={Boolean(item)}
        onClose={onClose}
        title={modalTitle}
        description={modalDescription}
        footer={footerActions}
        size={fullAccess ? "default" : "default"}
      >
        {loading ? (
          <LoadingSpinner label="Loading…" className="py-8" />
        ) : item.kind === "income" && receipt ? (
          <div className="grid gap-4">
            {actionError ? <p className="edlab-error">{actionError}</p> : null}
            <div className="flex items-center justify-between">
              <Badge tone="success">Received</Badge>
              <span className="text-sm text-edlab-muted">{receipt.receiptDate}</span>
            </div>
            <p className="text-[31px] font-semibold tracking-[-1px] text-edlab-ink">
              {formatMoney(receipt.total)}
            </p>
            {!fullAccess ? (
              <p className="text-sm text-edlab-muted">
                Showing assigned allocations only.
              </p>
            ) : null}
            <Table caption="Receipt allocations">
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Programme</TableHeaderCell>
                  <TableHeaderCell>Funding</TableHeaderCell>
                  <TableHeaderCell className="text-right">Amount</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {receipt.lines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell>{maps.unitName(line.unitId)}</TableCell>
                    <TableCell>{maps.fundName(line.fundId)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMoney(line.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {fullAccess && receipt.note ? (
              <p className="text-sm text-edlab-muted">{receipt.note}</p>
            ) : null}
            {canReverseReceipt ? (
              <div className="rounded-xl border border-edlab-line bg-edlab-bg/50 p-4">
                <h3 className="mb-3 font-semibold text-edlab-ink">Reverse receipt</h3>
                <div className="flex flex-wrap gap-2">
                  <input
                    className="edlab-input min-w-[200px] flex-1 text-sm"
                    placeholder="Reversal reason"
                    value={reverseReason}
                    onChange={(event) => {
                      setReverseReason(event.target.value);
                    }}
                  />
                  <Button
                    variant="danger"
                    disabled={reverseReceipt.isPending}
                    onClick={() =>
                      void runAction(() =>
                        reverseReceipt.mutateAsync({ id: receipt.id, reason: reverseReason }),
                      )
                    }
                  >
                    Reverse receipt
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        ) : expense ? (
          <div className="grid gap-4">
            {actionError ? <p className="edlab-error">{actionError}</p> : null}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Badge tone={item.partial ? "neutral" : expenseStatusTone(expense.status)}>
                {item.partial ? "Scoped detail" : formatExpenseStatus(expense.status)}
              </Badge>
              <span className="text-sm text-edlab-muted">{expense.expenseDate}</span>
            </div>
            <p className="text-[31px] font-semibold tracking-[-1px] text-edlab-ink">
              {formatMoney(expense.total)}
            </p>
            <p className="text-sm text-edlab-muted">
              {item.partial
                ? "Only your assigned allocation lines are shown."
                : expense.description ?? "No additional description."}
            </p>
            {fullAccess ? (
              <div className="activity-strip flex flex-wrap items-center gap-2 text-[13px] text-[#8a9699]">
                <b className="rounded-md bg-[#eef6f1] px-2.5 py-1.5 font-medium text-[#437c64]">
                  {expense.createdBy.name}
                </b>
                <span aria-hidden>→</span>
                <span>
                  {expense.approvedBy
                    ? `Approved by ${expense.approvedBy.name}`
                    : "Awaiting independent approval"}
                </span>
              </div>
            ) : null}
            <Table caption="Expense allocations">
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Programme</TableHeaderCell>
                  <TableHeaderCell>Funding</TableHeaderCell>
                  <TableHeaderCell>Category</TableHeaderCell>
                  <TableHeaderCell className="text-right">Amount</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expense.lines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell>{maps.unitName(line.unitId)}</TableCell>
                    <TableCell>{maps.fundName(line.fundId)}</TableCell>
                    <TableCell>{maps.categoryName(line.categoryId)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMoney(line.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {fullAccess ? (
              <>
                <div className="flex items-center justify-between gap-3 border-t border-edlab-line pt-4">
                  <div>
                    <b className="block text-sm font-semibold text-edlab-ink">
                      Supporting evidence
                    </b>
                    <p className="mt-1 text-sm text-edlab-muted">
                      {expense.files.length
                        ? expense.files.map((file) => file.originalName).join(", ")
                        : expense.missingEvidenceReason ?? "Pending attachment"}
                    </p>
                  </div>
                  {expense.files.length ? (
                    <div className="flex flex-wrap items-center gap-3">
                      <a
                        href={fileDownloadUrl(expense.files[0]!.id)}
                        className="text-sm font-medium text-edlab-green hover:underline"
                        download
                      >
                        Download file
                      </a>
                      {canAttachEvidence ? (
                        <Button
                          variant="ghost"
                          size="small"
                          className="px-0 text-edlab-muted hover:bg-transparent"
                          disabled={deleteFile.isPending}
                          onClick={() =>
                            void runAction(async () => {
                              await deleteFile.mutateAsync(expense.files[0]!.id);
                              refreshExpense();
                            })
                          }
                        >
                          Remove file
                        </Button>
                      ) : null}
                    </div>
                  ) : (
                    <Badge tone="neutral">Pending</Badge>
                  )}
                </div>
                {expense.rejectionReason ? (
                  <p className="edlab-notice">Reviewer note: {expense.rejectionReason}</p>
                ) : null}
                {canWithdrawSubmitted || canWithdrawApproved ? (
                  <Button
                    variant="secondary"
                    disabled={withdrawExpense.isPending}
                    onClick={() =>
                      void runAction(() => withdrawExpense.mutateAsync({ id: expense.id }))
                    }
                  >
                    Withdraw to draft
                  </Button>
                ) : null}
                {canReverseExpense ? (
                  <div className="rounded-xl border border-edlab-line bg-edlab-bg/50 p-4">
                    <h3 className="mb-3 font-semibold text-edlab-ink">
                      Reverse approved expense
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      <input
                        className="edlab-input min-w-[200px] flex-1 text-sm"
                        placeholder="Reversal reason"
                        value={expenseReverseReason}
                        onChange={(event) => {
                          setExpenseReverseReason(event.target.value);
                        }}
                      />
                      <Button
                        variant="danger"
                        disabled={reverseExpense.isPending || !expenseReverseReason.trim()}
                        onClick={() =>
                          void runAction(() =>
                            reverseExpense.mutateAsync({
                              id: expense.id,
                              reason: expenseReverseReason,
                            }),
                          )
                        }
                      >
                        Reverse expense
                      </Button>
                    </div>
                  </div>
                ) : null}
                {payments.length > 0 ? (
                  <div>
                    <div className="mb-3 flex items-center justify-between">
                      <h3 className="font-semibold text-edlab-ink">Payment history</h3>
                      <span className="text-sm text-edlab-muted">
                        {formatMoney(expense.paidAmount)} paid
                      </span>
                    </div>
                    <Table caption="Expense payments">
                      <TableHead>
                        <TableRow>
                          <TableHeaderCell>Date</TableHeaderCell>
                          <TableHeaderCell>Voucher</TableHeaderCell>
                          <TableHeaderCell>Account</TableHeaderCell>
                          <TableHeaderCell className="text-right">Paid</TableHeaderCell>
                          {canReverse ? <TableHeaderCell> </TableHeaderCell> : null}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {payments.map((payment) => (
                          <TableRow key={payment.id}>
                            <TableCell>{payment.paymentDate}</TableCell>
                            <TableCell>{payment.voucherNo}</TableCell>
                            <TableCell>{maps.bankName(payment.bankAccountId)}</TableCell>
                            <TableCell className="text-right tabular-nums">
                              {formatMoney(payment.amount)}
                            </TableCell>
                            {canReverse ? (
                              <TableCell>
                                <div className="flex flex-wrap gap-2">
                                  <input
                                    className="edlab-input min-w-[100px] py-1 text-sm"
                                    placeholder="Reason"
                                    value={paymentReverseReason[payment.id] ?? ""}
                                    onChange={(event) => {
                                      setPaymentReverseReason((prev) => ({
                                        ...prev,
                                        [payment.id]: event.target.value,
                                      }));
                                    }}
                                  />
                                  <Button
                                    variant="danger"
                                    size="small"
                                    disabled={reversePayment.isPending}
                                    onClick={() =>
                                      void runAction(() =>
                                        reversePayment.mutateAsync({
                                          id: payment.id,
                                          reason: paymentReverseReason[payment.id] ?? "",
                                        }),
                                      )
                                    }
                                  >
                                    Reverse
                                  </Button>
                                </div>
                              </TableCell>
                            ) : null}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-sm text-edlab-muted">No payment has been recorded.</p>
                )}
              </>
            ) : (
              <p className="edlab-notice">
                Full voucher details and attachments require access to every unit included in this
                expense.
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-edlab-muted">
            Transaction not found or outside your scope.
          </p>
        )}
      </Modal>

      {expense && fullAccess ? (
        <>
          <RecordPaymentModal
            open={subModal === "payment"}
            onClose={() => {
              setSubModal(null);
            }}
            expense={expense}
            payments={payments}
            bankAccounts={banks.data?.bankAccounts ?? []}
            onRecorded={refreshExpense}
          />
          <RejectExpenseModal
            open={subModal === "reject"}
            onClose={() => {
              setSubModal(null);
            }}
            expenseId={expense.id}
            onRejected={refreshExpense}
          />
          <AttachEvidenceModal
            open={subModal === "evidence"}
            onClose={() => {
              setSubModal(null);
            }}
            expenseId={expense.id}
            expenseReference={expense.referenceNo}
            payee={expense.payee}
            onUploaded={refreshExpense}
          />
          <EditExpenseModal
            open={subModal === "edit"}
            onClose={() => {
              setSubModal(null);
            }}
            expense={expense}
            units={units}
            funds={funds}
            categories={expenseCategories}
            onSaved={refreshExpense}
          />
        </>
      ) : null}
    </>
  );
}
